import {
  KIOSK_LANGUAGES,
  SWITCHER_LIVE,
  isKioskLanguage,
  languageInfo,
} from "./languages";
import { useLanguage, useT } from "./LanguageProvider";

/**
 * G-85 — the lock screen's language dropdown (owner ruling 2026-09-20,
 * replacing the five buttons this used to be).
 *
 * It lives on the unarmed and PIN screens ONLY. There is deliberately no
 * language control inside the hub: once a florist has PINned in, the language
 * is theirs and comes from their own record, and a second control on the
 * counter would just be a way to change it by accident.
 *
 * The first option is not a language. "Staff member's language" is the
 * dropdown's resting state and what it returns to when the screen locks, so a
 * pick is a ONE-SHIFT override rather than something the next florist
 * inherits. Picking an actual language also becomes what the lock screen
 * itself shows from then on, which is how a Russian manager's shift leaves
 * the tablet speaking Russian to the Russians who follow them.
 *
 * That memory is why the resting option NAMES the language it will show:
 * "Язык сотрудника (Русский)". Without it the dropdown said one thing while
 * the screen showed another, and the state driving the difference was
 * invisible — the owner found it in ten seconds ("why on default there is
 * Russian language?"). The parenthetical always matches the surrounding
 * text's language, because both are rendered in whatever is on screen.
 *
 * Each option is named in its OWN language — a Ukrainian florist looks for
 * "Українська", never for "Ukrainian" (the G-27 rule for the dictation
 * select). `lang` on each option tells a screen reader to switch voice.
 */

/** The dropdown's "no language chosen" value. Not a language code. */
const AUTO_VALUE = "__auto__";

export function LanguageSwitcher() {
  const { language, hasOverride, chooseLanguage } = useLanguage();
  const { t } = useT();

  if (!SWITCHER_LIVE && !import.meta.env.DEV) return null;

  return (
    <div className="shop-floor-lang-switcher">
      <label className="visually-hidden" htmlFor="shop-floor-lang-select">
        {t("lang.label")}
      </label>
      <select
        id="shop-floor-lang-select"
        className="shop-floor-lang-select"
        // The SELECTED value is the override, not the effective language: the
        // screen may be showing Spanish because the tablet remembers Spanish
        // while nobody has overridden anything, and the dropdown must say so.
        value={hasOverride ? language : AUTO_VALUE}
        onChange={(e) => {
          const next = e.target.value;
          chooseLanguage(isKioskLanguage(next) ? next : null);
        }}
      >
        <option value={AUTO_VALUE}>
          {t("lang.staffLanguageNow", { language: languageInfo(language).label })}
        </option>
        {KIOSK_LANGUAGES.map((option) => (
          <option key={option.code} value={option.code} lang={option.intl}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
